import {
  SnackBarState,
  SnackBarToast,
  ToastErrorPayload,
  ToastSuccessPayload,
  ToastInfoPayload,
  ToastWarningPayload,
} from "@/shared/types/global";
import { defineStore } from "pinia";
import { getErrorMessage } from "@/shared/utils/errorMessage";

let nextToastId = 1;

const getSafeWindowOrigin = (): string => {
  if (typeof window === "undefined") {
    return "http://localhost";
  }

  const origin = window.location.origin;
  return origin && origin !== "null" ? origin : "http://localhost";
};

const resolveBasePathname = (): string => {
  const baseUrl = import.meta.env.BASE_URL || "/";

  try {
    return new URL(baseUrl, getSafeWindowOrigin()).pathname;
  } catch {
    if (!baseUrl) {
      return "/";
    }

    return baseUrl.startsWith("/") ? baseUrl : `/${baseUrl}`;
  }
};

const getAppRelativePathname = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const pathname = window.location.pathname || "/";
  const resolvedBasePath = resolveBasePathname();
  const normalizedBasePath = resolvedBasePath.endsWith("/")
    ? resolvedBasePath
    : `${resolvedBasePath}/`;

  if (!normalizedBasePath || normalizedBasePath === "/") {
    return pathname;
  }

  if (!pathname.startsWith(normalizedBasePath)) {
    return pathname;
  }

  const relativePath = pathname.slice(normalizedBasePath.length);
  return `/${relativePath.replace(/^\/+/, "")}`;
};

const isKioskPath = (): boolean => getAppRelativePathname().startsWith("/kiosk/");

export const useSnackBarStore = defineStore("snackbar", {
  state: (): SnackBarState => ({
    defaultTimeout: 5000,
    toasts: [],
  }),
  actions: {
    enqueueToast(
      payload: Omit<SnackBarToast, "id" | "mode" | "timeout"> & { timeout?: number },
    ): number {
      const timeout = payload.timeout ?? this.defaultTimeout;
      const id = nextToastId++;
      const mode = isKioskPath() ? "kiosk" : "default";
      this.toasts.push({
        id,
        mode,
        color: payload.color,
        timeout,
        message: payload.message,
        icon: payload.icon,
        title: payload.title,
        subtitle: payload.subtitle,
        image: payload.image,
        imageAlt: payload.imageAlt,
        promise: payload.promise,
        success: payload.success,
        error: payload.error,
      });

      return id;
    },
    dismissToast(id: number): void {
      this.toasts = this.toasts.filter((toast) => toast.id !== id);
    },
    clearToasts(): void {
      this.toasts = [];
    },
    toastSuccess(payload: ToastSuccessPayload): void {
      this.enqueueToast({
        message: payload.message,
        color: "success",
        icon: "mdi-check",
        title: payload.title,
        subtitle: payload.subtitle,
        image: payload.image,
        imageAlt: payload.imageAlt,
        timeout: payload.timeout,
      });
    },
    toastInfo(payload: ToastInfoPayload): void {
      this.enqueueToast({
        message: payload.message,
        color: "info",
        icon: "mdi-information-outline",
      });
    },
    toastWarning(payload: ToastWarningPayload): void {
      this.enqueueToast({
        message: payload.message,
        color: "warning",
        icon: "mdi-alert",
      });
    },
    toastError(payload?: ToastErrorPayload): void {
      const { message, code, error } = payload ?? {};

      this.enqueueToast({
        message: getErrorMessage({ message, error }, code),
        color: "error",
        icon: "mdi-alert-circle-outline",
        timeout: payload?.timeout ?? (isKioskPath() ? 10000 : undefined),
      });
    },
  },
});
